import { z } from 'zod'
import {
  INSURANCE_EXCESS_FEE_TYPE_VALUES,
  type InsuranceExcessFeeType,
} from '@/models/insurance.js'
import {
  PatientFieldName,
  PATIENT_FIELD_NAME_TO_FORM_FIELD,
} from '@/models/patients.js'
import { safeRegExp } from '@/utils/regex.js'
import { isTargetValueSelected } from '@/utils/boolean.js'

/** Patient insurance fields as used on the patient details form (camelCase). */
export type PatientInsuranceInfo = {
  insuranceCompany?: string
  insuranceNumber?: string
  insuranceExcessFeeType?: InsuranceExcessFeeType
  insuranceExcessFeeAmount?: number
  insurancePolicyEndDate?: string
}

export type PatientIfInsuranceRegulations = {
  companyId: number | string | null | undefined
  numberValidPattern: string | null | undefined
  verificationCustomFieldId: number | string | null | undefined
}

/** Zod / vee-validate path for the insurance policy number field. */
export const patientFormInsuranceNumberField = PATIENT_FIELD_NAME_TO_FORM_FIELD[
  PatientFieldName.Insurance
] as string

export function patientInsuranceFormZodSchema(options: {
  insuranceNumber: z.ZodString | z.ZodOptional<z.ZodString>
  invalidPolicyEndDateMessage: string
  ifInsuranceNumberInvalidMessage: string
  requiredMessage: string
  isIfInsuranceVerificationFieldRequired: () => boolean
  getIfInsuranceRegs: () => Pick<
    PatientIfInsuranceRegulations,
    'companyId' | 'numberValidPattern'
  >
}) {
  const {
    insuranceNumber,
    invalidPolicyEndDateMessage,
    ifInsuranceNumberInvalidMessage,
    requiredMessage,
    isIfInsuranceVerificationFieldRequired,
    getIfInsuranceRegs,
  } = options

  return z
    .object({
      insuranceCompany: z.string().optional(),
      insuranceNumber,
      insuranceExcessFeeType: z
        .enum(INSURANCE_EXCESS_FEE_TYPE_VALUES)
        .optional(),
      insuranceExcessFeeAmount: z.number().optional(),
      insurancePolicyEndDate: z
        .string()
        .optional()
        .refine(
          (val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val),
          invalidPolicyEndDateMessage,
        ),
    })
    .superRefine((data, ctx) => {
      const isIfVerificationFieldRequired =
        isIfInsuranceVerificationFieldRequired()
      const hasInsuranceCompany = Boolean(data.insuranceCompany?.trim())
      const hasInsuranceNumber = Boolean(data.insuranceNumber?.trim())

      if (isIfVerificationFieldRequired) {
        if (!hasInsuranceCompany) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredMessage,
            path: ['insuranceCompany'],
          })
        }

        if (!hasInsuranceNumber) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredMessage,
            path: [patientFormInsuranceNumberField],
          })
        }
      }

      if (
        (!isIfVerificationFieldRequired || hasInsuranceNumber) &&
        !patientFormPassesIfInsuranceNumberPattern(
          data.insuranceCompany,
          data.insuranceNumber,
          getIfInsuranceRegs(),
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ifInsuranceNumberInvalidMessage,
          path: [patientFormInsuranceNumberField],
        })
      }
    })
}

export function compileIfInsuranceNumberPattern(
  pattern: string | null | undefined,
): RegExp | null {
  return safeRegExp(pattern)
}

/**
 * When the configured IF company is selected, require the number to match the
 * pattern regulation when present; otherwise the refinement passes.
 */
export function patientFormPassesIfInsuranceNumberPattern(
  insuranceCompany: number | string | undefined,
  insuranceNumber: string | undefined,
  regulations: Pick<
    PatientIfInsuranceRegulations,
    'companyId' | 'numberValidPattern'
  >,
): boolean {
  const { companyId, numberValidPattern } = regulations
  if (!isTargetValueSelected(insuranceCompany?.toString(), companyId)) {
    return true
  }
  const expression = compileIfInsuranceNumberPattern(
    numberValidPattern ?? undefined,
  )
  return expression ? expression.test(insuranceNumber ?? '') : true
}

/**
 * IF verification checkbox + company + number rules for submit-time validation.
 * Skips when company id or verification custom field id is missing from config.
 */
export function patientFormPassesIfInsuranceVerification(
  insuranceCompany: string | undefined,
  insuranceNumber: string | undefined,
  customFields: Record<string, string> | undefined,
  regulations: PatientIfInsuranceRegulations,
): boolean {
  const { companyId, numberValidPattern, verificationCustomFieldId } =
    regulations
  if (!companyId || !verificationCustomFieldId) {
    return true
  }

  const checkboxValue = customFields?.[verificationCustomFieldId]
  const isChecked = checkboxValue === 'true'
  const hasIFCompany = isTargetValueSelected(insuranceCompany, companyId)
  const patternExpression = compileIfInsuranceNumberPattern(
    numberValidPattern ?? undefined,
  )
  const hasValidNumber = patternExpression
    ? patternExpression.test(insuranceNumber ?? '')
    : Boolean(insuranceNumber?.trim())

  if (isChecked && (!hasIFCompany || !hasValidNumber)) {
    return false
  }
  if (hasIFCompany && !hasValidNumber) {
    return false
  }
  return true
}

/**
 * LähiTapiola soft validation: non-empty number that fails format heuristics.
 */
export function lahitapiolaInsuranceNumberShouldWarn(
  trimmedNumber: string,
): boolean {
  if (!trimmedNumber) {
    return false
  }
  return (
    !/^\d+$/.test(trimmedNumber) ||
    trimmedNumber.length !== 10 ||
    trimmedNumber.startsWith('307')
  )
}
